/*
 * @Author: Tahiti
 * @Date: 2025-10-22 18:00:00
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-05 15:27:30
 * @Description: 视频工具相关API接口
 * @FilePath: /weryai-web/api/video-tools.ts
 */
import { http } from '~/utils/request'

// ========== 视频模板配置接口 ==========

/**
 * @description 获取视频模板配置
 * @param category 分类代码，如：ai-kissing, ai-dancing
 */
export const getVideoTemplateConfigsApi = (category: string) => {
    return http.get(
        `/api/v1/auth/creative/video/template/configs?category=${category}`
    )
}

export const createVideoTemplateApi = (params: {
    bgm?: boolean
    copyright_protection?: boolean
    image_url?: Array<string>
    public_visible?: boolean
    template_id: number
    aspect_ratio?: string
}) => {
    return http.post('/api/v1/auth/creative/video/template', params)
}
// ========== 视频换脸相关接口 ==========

/**
 * @description 视频换脸
 * @param data 换脸参数
 */
export const videoFaceChangeApi = (data: {
    image_url: string
    video_url: string
    model?: string
    team_id?: string
    platform?: string
    request_id?: string
    t?: number
}) => {
    return http.post('/api/v1/auth/video/face/change', data)
}

/**
 * @description 获取任务结果
 * @param taskId 任务ID
 */
export const getTaskResultApi = (taskId: string) => {
    return http.get(`/backend/auth/task/${taskId}`)
}

/**
 * @description 获取任务详情
 * @param id 日志ID
 */
export const getTaskResultDetailApi = (id: string | number) => {
    return http.get(`/backend/auth/ad-creative-log/${id}`)
}

/**
 * @description 上报任务结果
 * @param data 上报数据
 */
export const reportTaskResultApi = (data: {
    task_id: string
    creative_id: string | number
}) => {
    return http.post('/backend/auth/task/display-report', data)
}

/**
 * @description 取消任务
 * @param taskId 任务ID
 */
export const cancelTaskApi = (taskId: string) => {
    return http.post(`/backend/auth/task/cancel/${taskId}`, {})
}

// ========== 视频对口型相关接口 ==========

/**
 * @description 获取TTS声线列表
 * @param productId 产品ID，传 -1 只获取系统内置声线
 * @param lang 语言代码，如 zh(中文)、en(英文)
 */
export const getTTSVoiceListApi = (productId: number = -1, lang?: string) => {
    // http.get 第二参就是 Query，不要再包一层 { params }（那是 axios 写法）
    return http.get(
        `/api/v1/auth/tts-voice/app/${productId}`,
        lang ? { lang } : undefined
    )
}

/**
 * @description 生成TTS音频
 */
export const generateTTSAudioApi = (data: {
    id: number
    text: string
    speed?: string
    vol?: string
}) => {
    return http.post('/api/v1/auth/tts-voice/generate', data)
}

/**
 * @description 视频对口型
 * @param data 对口型参数
 */
export const videoLipSyncApi = (data: {
    video_url: string
    audio_url: string
    model?: string
    team_id?: string
    platform?: string
    request_id?: string
    t?: number
}) => {
    return http.post('/api/v1/auth/video/lips/change', data)
}

// ========== 视频去水印相关接口 ==========

/**
 * @description 视频去水印
 * @param data 去水印参数
 */
export const videoRemoveWatermarkApi = (data: {
    video_url?: string
    rect_volist?: Array<{
        lt_x: number
        lt_y: number
        rb_x: number
        rb_y: number
        width: number
        height: number
    }>
    team_id?: string
    platform?: string
    t?: number
}) => {
    return http.post('/api/v1/auth/video/watermark/remove', data)
}

/**
 * @description 视频字幕擦除
 * @param data 字幕擦除参数
 */
export const videoSubtitleEraseApi = (data: {
    video_url: string
    rect_volist?: Array<{
        lt_x: number
        lt_y: number
        rb_x: number
        rb_y: number
        width: number
        height: number
    }>
    team_id?: string
    platform?: string
    product_id?: number
    copyright_protection?: boolean
    public_visible?: boolean
    t?: number
}) => {
    return http.post('/api/v1/auth/video/subtitle/erase', data)
}

// ========== 视频动漫化相关接口 ==========

/**
 * @description 视频动漫化
 * @param data 动漫化参数
 */
export const videoToAnimeApi = (data: {
    video_url: string
    video_style: string
    team_id?: string
    anime_flag: true
    lang: 'en'
    platform?: string
    request_id?: string
    t?: number
}) => {
    return http.post('/api/v1/auth/video/magic', data)
}

// ========== 视频翻译相关接口 ==========

export const videoSubtitleTranslateApi = (data: {
    target_language: string
    team_id?: string
    video_url: string
}) => {
    return http.post('/api/v1/auth/video/subtitle/translate', data)
}

// ========== 视频增强模型接口 ==========

export const videoUpscalerApi = (data: {
    team_id?: string
    video_url: string
    resolution?: string
    t?: number
    r?: string
}) => {
    return http.post('/api/v1/auth/video/upscaler', data)
}

// ========== 图片背景更换接口 ==========

export const imgBackgroundReplaceApi = (data: {
    team_id?: string
    img_url: string
    prompt: string
}) => {
    return http.post('/api/v1/auth/creative/img/img_bg_change', data)
}

// ========== 视频背景去除接口 ==========

export const videoBackgroundRemoveApi = (data: {
    team_id?: string
    video_url: string
    background_color: string
    output_container_and_codec: string
}) => {
    return http.post('/api/v1/auth/video/background/remove', data)
}

// ========== 图片文本删除接口 ==========

export const imgTextRemoveApi = (data: {
    img_url: string
    orig_img_url: string
    team_id?: string // 可选，会自动添加
    platform?: string
    request_id?: string
    copyright_protection?: boolean
    public_visible?: boolean
    product_id?: number // 可选，会自动添加
    model?: string
}) => {
    return http.post('/api/v1/auth/creative/img/img_text_erase', data)
}

// ========== 视频延长器接口 ==========

export const videoExtenderApi = (data: {
    team_id?: string
    video_url?: string
    duration: number
    prompt: string
    resolution: string
    style: string
    t?: number
}) => {
    return http.post('/api/v1/auth/video/extend', data)
}

// 视频动作模仿
export const createVideoMotionImitationApi = (data: {
    copyright_protection: boolean
    image_url: string
    public_visible: boolean
    resolution: string
    type: 'move' | 'replace'
    video_url: string
}) => {
    return http.post('/api/v1/auth/video/anime/replace', data)
}
